import Component from '@glimmer/component';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import { VStack } from 'tio-reimagined/components/layout';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import Button from 'tio-reimagined/components/button';
import Card from 'tio-reimagined/components/card';
import FeatureCard from 'tio-reimagined/components/feature-card';
import { eq } from 'tio-ui/utilities';
import SectionHeader from 'tio-reimagined/components/section-header';
import SmartActionsSection from 'tio-employee/components/reimagined/smart-actions-section';
import type { SmartAction } from 'tio-employee/components/reimagined/smart-action-card';
import NavCard from 'tio-reimagined/components/nav-card';
import PageHero from 'tio-reimagined/components/page-hero';
import GlassCard from 'tio-reimagined/components/glass-card';
import CtaBanner from 'tio-reimagined/components/cta-banner';
import type { DataSource } from 'tio-employee/components/reimagined/data-indicator';
import type { DataSourceInfo } from 'tio-employee/services/reimagined-data';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import {
  PhPiggyBank,
  PhArrowRight,
  PhTrendUp,
  PhChartLineUp,
  PhHandshake,
  PhShieldCheck,
  PhTarget,
  PhBank,
  PhSparkle,
  PhRocket,
  PhCalculator,
  PhScales,
  PhRobot,
} from 'ember-phosphor-icons';

interface SyfIndexModel {
  dataSourceInfo: DataSourceInfo;
  employerContributions: {
    contributions: {
      yearToDate: number;
      lifetime: number;
      projectedAnnual: number;
      monthlyAmount: number;
      annualLimit: number;
    };
    isLiveData: boolean;
  };
}

interface SyfIndexSignature {
  Args: {
    model: SyfIndexModel;
  };
}

interface SyfInsight {
  title: string;
  value: string;
  description: string;
  intent: 'success' | 'warning' | 'info';
}

export default class SyfIndex extends Component<SyfIndexSignature> {
  @service declare reimaginedData: ReimaginedDataService;

  get isDemoMode() {
    return this.reimaginedData.isDemoMode;
  }

  get contributionsSource(): DataSource {
    if (this.isDemoMode) return 'demo';
    return this.args.model.employerContributions.isLiveData ? 'live' : 'placeholder';
  }

  get smartActions(): SmartAction[] {
    return [
      {
        id: 'enrollment',
        title: 'Enroll in 401(k)',
        description: 'Start building your retirement savings with automatic payroll deductions.',
        icon: PhPiggyBank,
        priority: 'urgent',
        category: 'money',
        cta: 'Enroll Now',
        route: 'authenticated.reimagined.advisor',
      },
      {
        id: 'maximize-match',
        title: 'Maximize Employer Match',
        description:
          "Don't leave free money on the table. Contribute enough to get the full match.",
        icon: PhHandshake,
        priority: 'high',
        category: 'opportunity',
        cta: 'Optimize Match',
        route: 'authenticated.reimagined.advisor',
      },
      {
        id: 'review-investments',
        title: 'Review Investments',
        description: 'Make sure your allocation matches your risk tolerance and timeline.',
        icon: PhChartLineUp,
        priority: 'medium',
        category: 'money',
        cta: 'Review Now',
        route: 'authenticated.reimagined.advisor',
      },
    ];
  }

  get insights(): SyfInsight[] {
    return [
      {
        title: 'Employer Match',
        value: '100% up to 6%',
        description: 'Your employer matches dollar-for-dollar on the first 6% of your salary',
        intent: 'success',
      },
      {
        title: 'Vesting Schedule',
        value: '3 years',
        description: "You're 67% vested. Full vesting at 3 years of service.",
        intent: 'info',
      },
      {
        title: 'Tax Savings',
        value: '$2,400/yr',
        description: 'Estimated annual tax savings from pre-tax contributions',
        intent: 'success',
      },
      {
        title: 'Retirement Readiness',
        value: '62%',
        description: 'On track for 62% of your retirement income goal',
        intent: 'warning',
      },
    ];
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="gap-8 pb-12">
      {{! Hero Section }}
      <PageHero @gradient="cool" @decoration="circles" @rounded="2xl">
        <:default>
          <div class="tio-row mb-4">
            <div class="tio-row-center h-12 w-12 rounded-xl bg-white/20">
              <PhPiggyBank class="h-7 w-7 text-white" />
            </div>
            <div>
              <Heading @level={{2}} class="text-white font-bold">
                Secure Your Future
              </Heading>
              <Text @size="sm" class="text-emerald-100">
                Retirement savings & 401(k) management
              </Text>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
            <GlassCard @label="Account Balance" @value="$47,850">
              <:subtext>
                <PhTrendUp class="h-3 w-3 text-emerald-300" />
                <span>+12.3% YTD</span>
              </:subtext>
            </GlassCard>
            <GlassCard @label="Your Contribution" @value="8%" @subtext="$4,800/year" />
            <GlassCard
              @label="Employer Match"
              @value="$3,600"
              @accent="default"
              @subtext="Free money earned"
            />
            <GlassCard @label="Projected at 65" @value="$1.2M" @subtext="At current rate" />
          </div>
        </:default>
      </PageHero>

      {{! Smart Actions }}
      <SmartActionsSection
        @actions={{this.smartActions}}
        @title="Next Steps"
        @subtitle="Actions to secure your financial future"
      />

      {{! Insights Grid }}
      <SectionHeader
        @icon={{PhChartLineUp}}
        @title="Your 401(k) Insights"
        @subtitle="Key metrics at a glance"
        @iconClass="tio-fg-success"
      />
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {{#each this.insights as |insight|}}
          <Card>
            <Text @size="xs" class="tio-fg-muted uppercase tracking-wide">{{insight.title}}</Text>
            <div
              class="text-xl font-bold mt-1
                {{if
                  (eq insight.intent 'success')
                  'tio-fg-success'
                  (if (eq insight.intent 'warning') 'tio-fg-warning' 'tio-fg-info')
                }}"
            >
              {{insight.value}}
            </div>
            <Text @size="xs" class="tio-fg-muted mt-2">
              {{insight.description}}
            </Text>
          </Card>
        {{/each}}
      </div>

      {{! Optimization Tips }}
      <SectionHeader
        @icon={{PhSparkle}}
        @title="Optimization Tips"
        @subtitle="Ways to boost your savings"
        @iconClass="tio-fg-brand-accent"
      />
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FeatureCard
          @icon={{PhRocket}}
          @title="Increase by 1%"
          @description="Increasing your contribution by just 1% could add $150,000+ to your retirement. Most people don't even notice the difference in their paycheck."
          @color="emerald"
        />
        <FeatureCard
          @icon={{PhShieldCheck}}
          @title="Roth vs Traditional"
          @description="Consider a Roth 401(k) if you expect to be in a higher tax bracket in retirement. You pay taxes now but withdrawals are tax-free."
          @color="blue"
        />
        <FeatureCard
          @icon={{PhTarget}}
          @title="Target-Date Funds"
          @description="Set it and forget it. Target-date funds automatically adjust your portfolio mix as you approach retirement."
          @color="purple"
        />
        <FeatureCard
          @icon={{PhSparkle}}
          @title="Loan vs Hardship"
          @description="Need emergency funds? A 401(k) loan is usually better than a hardship withdrawal. You repay yourself with interest."
          @color="amber"
        />
      </div>

      {{! Tools }}
      <SectionHeader
        @icon={{PhBank}}
        @title="Retirement Tools"
        @subtitle="Plan smarter"
        @iconClass="tio-fg-info"
      />
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <NavCard
          @layout="gradient"
          @title="Retirement Calculator"
          @subtitle="Project your retirement savings based on contributions and growth"
          @icon={{PhCalculator}}
          @route="authenticated.reimagined.finance"
        />
        <NavCard
          @layout="gradient"
          @title="Section 127 Optimizer"
          @subtitle="Balance student loans and retirement savings for maximum benefit"
          @icon={{PhScales}}
          @route="authenticated.reimagined.finance.optimizer"
        />
        <NavCard
          @layout="gradient"
          @title="Financial Advisor"
          @subtitle="Get AI-powered guidance on your overall financial strategy"
          @icon={{PhRobot}}
          @route="authenticated.reimagined.advisor"
        />
      </div>

      {{! CTA }}
      <CtaBanner
        @heading="Balance Student Loans & Retirement"
        @description="Our Section 127 Optimizer helps you allocate your employer benefit between loan repayment and retirement savings for maximum impact."
        @intent="success"
      >
        <:action>
          <LinkTo @route="authenticated.reimagined.finance.optimizer">
            <Button @intent="primary">
              Optimize My Benefits
              <PhArrowRight class="ml-2 h-4 w-4" />
            </Button>
          </LinkTo>
        </:action>
      </CtaBanner>
    </VStack>
  </template>
}
